import { Link, useParams } from "react-router-dom";
import { useCustomer, useDecidePromotion, useProfiles, usePromotions } from "../../api/hooks";
import { useAuth } from "../../auth/AuthProvider";
import { EnvironmentGate, useEnvironmentName, useRememberEnvironment } from "../../components/Environment";
import { Card, DataTable, EmptyState, ErrorState, LoadingState, PageHeader, RelativeTime, StatusPill, btnPrimary, btnSecondary } from "../../components/ui";
import { moduleById } from "../../lib/modules";
import { ContextBar } from "./ContextBar";

const TONE = { pending_approval: "warning", applied: "ok", rejected: "critical" } as const;

/**
 * Every change request into this environment (ADR-0008): a promotion, a
 * gated direct edit or a rollback waiting for a manager, and what was
 * decided before. Approving is the manager-only act; the API refuses
 * anyone else, and says why.
 */
export function PromotionsPage() {
  const { environmentId } = useParams();
  useRememberEnvironment(environmentId);
  if (!environmentId) return <EnvironmentGate module={moduleById("flow-configuration")} section="promotions" />;
  return <Promotions environmentId={environmentId} />;
}

function Promotions({ environmentId }: { environmentId: string }) {
  const { me } = useAuth();
  const env = useCustomer(environmentId);
  const profiles = useProfiles(environmentId);
  const q = usePromotions(environmentId);
  const decide = useDecidePromotion(environmentId);
  const envName = env.data?.customer.friendly_name ?? environmentId;
  const envNameOf = useEnvironmentName();
  const nameOf = (profileId: string) => profiles.data?.profiles.find((p) => p.profileId === profileId)?.name ?? profileId;
  const pending = (q.data?.promotions ?? []).filter((p) => p.status === "pending_approval");
  const readOnly = me?.viewAs?.mode === "read";

  return (
    <>
      <PageHeader title="Flow configuration · Promotions" />
      <div className="grid gap-5">
        <ContextBar environmentId={environmentId} environmentName={envName} live={env.data?.customer.live ?? false} onEnvironment={(id) => `/flow-configuration/${id}/promotions`}>
          <span className="text-sm text-neutral-600 dark:text-neutral-400">
            {q.data ? `${pending.length} waiting for a decision` : ""}
            {env.data && !env.data.customer.changes_require_approval && " · this environment does not gate changes"}
          </span>
        </ContextBar>
        {q.isPending && <LoadingState label="Loading change requests" />}
        {q.isError && <ErrorState error={q.error} onRetry={() => void q.refetch()} />}
        {q.data && (
          <>
            {pending.length > 0 && (
              <Card title="Waiting for a decision">
                <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
                  {pending.map((p) => (
                    <li key={p.promotionId} className="flex flex-wrap items-center justify-between gap-3 py-3">
                      <div className="text-sm">
                        <strong>{nameOf(p.profileId)}</strong> · {describeKind(p.kind, envNameOf(p.sourceEnvironmentId))} into {envName}
                        <span className="block text-xs text-neutral-500">
                          {p.requestedBy} · <RelativeTime iso={p.requestedAt} /> ·{" "}
                          <Link to={`/flow-configuration/${environmentId}/profiles/${p.profileId}/promote`} className="underline">see the diff</Link>
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <button type="button" className={btnPrimary} disabled={decide.isPending || readOnly} onClick={() => decide.mutate({ promotionId: p.promotionId, approve: true })}>
                          Approve and apply to {envName}
                        </button>
                        <button type="button" className={btnSecondary} disabled={decide.isPending || readOnly} onClick={() => decide.mutate({ promotionId: p.promotionId, approve: false })}>
                          Reject
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
                {decide.isSuccess && (
                  <p role="status" className="mt-3 text-sm text-green-800 dark:text-green-300">
                    {decide.data.status === "applied" ? `Applied as v${decide.data.configVersion} in ${envName}.` : "Rejected."}
                  </p>
                )}
                {decide.isError && <ErrorState error={decide.error} />}
              </Card>
            )}
            <Card title="Every change request">
              <DataTable
                caption={`Change requests into ${envName}, newest first`}
                rows={q.data.promotions}
                rowKey={(p) => p.promotionId}
                empty={<EmptyState title="No change requests yet" hint="A promotion, a gated edit or a rollback into this environment lands here, with what was decided." />}
                columns={[
                  { key: "profile", header: "Profile", render: (p) => <Link to={`/flow-configuration/${environmentId}/profiles/${p.profileId}`} className="underline">{nameOf(p.profileId)}</Link> },
                  { key: "what", header: "What", render: (p) => describeKind(p.kind, envNameOf(p.sourceEnvironmentId)) },
                  { key: "status", header: "Status", render: (p) => <StatusPill status={p.status.replace("_", " ")} tone={TONE[p.status]} /> },
                  { key: "requested", header: "Requested", render: (p) => <>{p.requestedBy} · <RelativeTime iso={p.requestedAt} /></> },
                  { key: "decided", header: "Decided", render: (p) => (p.decidedAt ? <>{p.decidedBy} · <RelativeTime iso={p.decidedAt} />{p.appliedVersion ? ` · v${p.appliedVersion}` : ""}</> : "-") },
                ]}
              />
            </Card>
          </>
        )}
      </div>
    </>
  );
}

function describeKind(kind: "promotion" | "direct_edit" | "rollback", source: string): string {
  switch (kind) {
    case "promotion": return `promotion from ${source}`;
    case "rollback": return "rollback";
    default: return "direct edit";
  }
}
